import React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Textarea from '@mui/joy/Textarea';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import Tooltip from '@mui/joy/Tooltip';

import FancyCheckbox from './FancyCheckbox';
import FileUpload from './FileUpload';
import { LoreDecorator } from './Lore';
import { Error } from 'components/FancyToast';

import { FileType } from 'stores/file_store';

import localforage from 'localforage';
import { forageGet, forageSet } from 'utils/misc';

export type CommentSubmitProps = {
    files: FileType[];
    content: string;
    content_type: string;
    ignoreQA?: string | null | undefined;
};

export type CommentInputProps = {
	// textAreaValue: string;
	// setTextAreaValue: (value: string) => void;
    uuid: string;
    clearId: string;
    onSubmit: ({}: CommentSubmitProps) => boolean;
    qaResponse?: MessageQAType | null | undefined;
    qaChecking?: boolean;
    showAi?: boolean;
    onAiEnabledChange?: ((enabled: boolean) => void) | null;
    onHide?:  ((hidden: boolean) => void) | null;
    comment?: CommentSubmitProps | null | undefined;
};

import { getIcon } from 'utils/misc';
import { MessageQAType } from 'stores/messages_store';
import LoadingSection from './LoadingSection';

const PREFIX = 'CI';
function _cache_key(uuid: string) {
    return `${PREFIX}/${uuid}`;
}

export function deleteCachedComment(uuid: string) {
     console.log("deleteing cached comment: %s", uuid);
    return localforage.removeItem(_cache_key(uuid));
}
function cacheComment(uuid: string, comment: CommentSubmitProps) {
    console.log("caching comment: %s, %o", uuid, comment);
    return localforage.setItem(_cache_key(uuid), comment);
}

async function getCachedComment(uuid: string) {
    console.log("getCachedComment: %s", uuid);
    return localforage.getItem<CommentSubmitProps>(_cache_key(uuid));
}

export default function CommentInput({
    uuid,
    onSubmit,
    showAi = false,
    onHide = null,
    comment = null,
    qaResponse = null,
    qaChecking = false,
    clearId = '',
    onAiEnabledChange = null,
}: CommentInputProps) {
    const textAreaRef = React.useRef<HTMLDivElement>(null);
    const [textAreaValue, setTextAreaValue] = React.useState('');
    const [isArmed, setIsArmed] = React.useState(false);
    const [enabling, setEnabling] = React.useState(false);
    const [disabling, setDisabling] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [files, setFiles] = React.useState<FileType[]>([] as FileType[]);
    const [hasFocus, setHasFocus] = React.useState(false);
    const [stateClearID, setStateClearID] = React.useState(clearId);
    const [ignoreQA, setIgnoreQA] = React.useState<string|null>(null);

    const [cachedAIEnabled, setCachedAIEnabled] = React.useState<boolean|null>(null);

    // console.log("CommentInput.: ", comment?.content, "textarea value:", textAreaValue);
    const CloseIcon = getIcon('Close');

    const handleClick = () => {

        if(isUploading) {
            Error('Please wait for the file to upload');
            return;
        }
        if (textAreaValue.trim() !== '') {
            console.log('submitting comment');
            if (onSubmit({
                ignoreQA: ignoreQA,
                files: files,
                content: textAreaValue.trim(),
                content_type: 'text/plain',
            } as CommentSubmitProps)) {
                console.log("Great! we submitted.");
            }
            // {
            //     setTextAreaValue('RESET');
            //     setUploadUuid(uploadUuid + 1);
            //     setFiles([] as FileType[]);
            //     console.log('unset files')
            // }
        }
    };

    React.useEffect(() => {
        if(comment?.files) {
            setFiles(comment.files);
        }
    }, [comment?.files]);

    React.useEffect(() => {
        if(!showAi) return;
        if (cachedAIEnabled === null) {
            forageGet<boolean>('ai:enabled')
                .then((value) => {
                    console.log("setting ai enabled from cache: %s", value);
                    setCachedAIEnabled(value);
                    setIsArmed(value || false);
                    onAiEnabledChange && onAiEnabledChange(value || false);
                });
        }
    }, [showAi]);


    React.useEffect(() => {
        if(clearId != stateClearID) {
            setStateClearID(clearId);
            setTextAreaValue('');
            setIgnoreQA(null);
            setFiles([] as FileType[]);
            deleteCachedComment(uuid);
        }
        else {
            console.log("clearId didn't change: %s", clearId);
        }
    }, [clearId]);

    React.useEffect(() => {
        getCachedComment(uuid).then((c: CommentSubmitProps | null) => {
            // .then((value: C ommentSubmitProps) => {
            // console.log("got cached comment: %o", c);
            setTextAreaValue(c?.content || '');
            if(c?.files) {
                setFiles(c.files);
            }
        });
    }, [uuid]);


    React.useEffect(() => {
        if(comment?.content) {
            setTextAreaValue(comment.content);
        }
    }, [comment?.content]);


    React.useEffect(() => {
        // this doesn't work?
        if (textAreaRef.current) {
            textAreaRef.current.focus();
        }
    }, []);


    React.useEffect(() => {
        doCache();
    }, [files.length]);

    const doCache = () => {
        cacheComment(uuid, {
            files: files,
            content: textAreaValue,
            content_type: 'text/plain',
        });
    };

    function handleFilesChange(f: FileType[]) {
        if (JSON.stringify(f) != JSON.stringify(files)) {
            setFiles(f);
            console.log("Set files and doingcache: ", f);
            doCache();
            // cacheComment(uuid, {
            //     files: f,
            //     content: textAreaValue,
            //     content_type: 'text/plain',
            // });
        }
        else {
        }
    }

    function handleTextChange(value: string) {
        setTextAreaValue(value);
    }

    function handleBlur() {
        console.log("handleBlur");
        doCache();
    }

    function doSetArmed(armed: boolean) {
        setIsArmed(armed);
        onAiEnabledChange && onAiEnabledChange(armed);
        forageSet('ai:enabled', armed);
        if (armed) {
            setEnabling(true);
            setDisabling(false);
            setTimeout(() => {
                setEnabling(false);
            }, 1000);
        }
        else {
            setEnabling(false);
            setDisabling(true);
            setTimeout(() => {
                setDisabling(false);
            }, 1000);
        }
    }

    const armedStyle = isArmed ? {
        transition: 'all 0.2s ease-in-out',
        '--Textarea-focusedInset': 'var(--any, )',
        '--Textarea-focusedThickness': '0.15rem',
        '--Textarea-focusedHighlight': 'rgba(13,110,253,.25)',
        '&::before': {
            transition: 'box-shadow .15s ease-in-out',
        },
        '&:focus-within': {
            borderColor: "#86b7fe",
        },
    } : {
        transition: 'all 0.2s ease-in-out',

    };
    return (
        <Sheet
            variant="soft"
            color={isArmed ? "primary" : "neutral"}
            sx={{
                transition: "border-width 0.3s ease, background-color 0.5s ease",
                position: "sticky",
                border: isArmed ? 3 : 0,
                // borderBottom: 0, // isArmed ?  1 : 0,
                borderRadius: 10,
                // borderBottom: isArmed ? 0 : 3 ,
                // borderRight: isArmed ? 0 : 3,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                bottom: 0,
                zIndex: 999,
                padding: 1,
            }}
        >


            {showAi &&
                <Stack
                    width="100%"
                    justifyContent="flex-end"
                    alignItems="center"
                    direction="row"
                    spacing={1}
                    pb={1}
                >

                    {enabling &&
                        <Typography level="body-xs">
                            AI Enabled.
                        </Typography>}

                    <Box flexGrow={1} />

                    <FancyCheckbox
                        size="sm"
                        label={<Typography
                            sx={{ "transition": "color 0.25s linear" }}
                            color={enabling ? "success" : (disabling ? "warning" : "neutral")}
                            level="body-xs">
                            {!isArmed ? "Enable AI" : "AI Enabled"}
                        </Typography>}
                        checked={isArmed}
                        onChange={(e) => doSetArmed(e.target.checked)}

                    />

                    {onHide &&
                        <Tooltip arrow placement="top" title="Hide this. You can always unhide it.">
                            <IconButton
                                size="sm"
                                onClick={() => onHide(true)}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    }
                </Stack>
            }
            { qaChecking &&
                <Stack
                    m={1}
                    spacing={1}
                >
                    <LoadingSection copy="Checking Response" />
                </Stack>

            }
            {!qaChecking && qaResponse && !qaResponse.response_pass &&
                <Stack
                    m={1}
                    spacing={1}
                >
                    <Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                    <Chip color="warning">
                        QA Issues
                    </Chip>

                    <Typography level="body-xs" color="warning">
                        Possible issues with your response.
                    </Typography>
                        </Stack>
                    <Typography level="body-sm">
                        {qaResponse.response_text}
                    </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <FancyCheckbox
                            size="sm"
                            label="Ignore QA Issues"
                            checked={ignoreQA ? true : false}
                            onChange={(e) => {
                                setIgnoreQA(e.target.checked ?
                                    qaResponse.uuid : null
                                );
                            }} />

                        <LoreDecorator slug="assistant/qa/ignore" />
                    </Stack>

                </Stack>
            }
      <FileUpload
                defaultFilesUploaded={files}
                uploadUuid={0}
                setIsUploading={setIsUploading}
                onFilesUploaded={handleFilesChange}
            />
            <Textarea
                placeholder="Type something here…"
                aria-label="Message"
                sx={armedStyle}
                ref={textAreaRef}

                onChange={(e) => {
                    setHasFocus(true);
                    handleTextChange(e.target.value);
                }}
                onFocus={() => {
                    setHasFocus(true);
                }}
                onBlur={() => {
                    setHasFocus(false);
                    handleBlur();
                }}

                value={textAreaValue}
                minRows={ hasFocus || textAreaValue.length ? 4 : 1 }
                maxRows={10}
                size="sm"
                endDecorator={

                    <Stack
                        sx={{
                            opacity: textAreaValue.length ? 1 : 0.3,
                            transition: "all 0.25s ease-in-out"
                        }}
                        height={30}
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        flexGrow={1}
                        p={1}
                        pb={2}
                        divider={<Divider orientation="vertical" />}
                        alignItems="center"
                    >

                        <Button
                            onClick={handleClick}>Send</Button>

                    </Stack>
                }
            />

        </Sheet>

    );
}
